/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import { EMPTY_ARRAY } from '@/redux/selectors/pieveSelectors/constants.js';
import { threadContentMap } from '@/redux/selectors/pieveSelectors/baseSelectors.js';
import findWorkflowStepTask from '@/redux/selectors/pieveSelectors/lib/findWorkflowStepTask.js';
import getThreadContentValue from '@/redux/selectors/pieveSelectors/lib/getThreadContentValue.js';
import { isThreadRunning, threadRunState, threadRunStartedAt } from '@/redux/selectors/pieveSelectors/threadRunSelectors.js';

// Shared empty identity: a fresh [] here would break memoization for every selector taking this as input.
export const workflowStepTasks = (state) => state?.pieve?.workflowStepTasks || EMPTY_ARRAY;

export const workflowStepTaskThreadId = (state, { workflowId, stepId } = {}) => {
  return findWorkflowStepTask(workflowStepTasks(state), { workflowId, stepId })?.threadId || null;
};

// The Test window's own chat thread for this workflow; null until POST .../test-thread comes back.
export const workflowTestThreadId = (state, workflowId) => state?.pieve?.workflowTestThreadIdByWorkflowId?.[String(workflowId || '').trim()] || null;

// True the moment the ask is sent, not when the round trip returns.
export const hasWorkflowStepTask = (state, { workflowId, stepId } = {}) => {
  return Boolean(findWorkflowStepTask(workflowStepTasks(state), { workflowId, stepId }));
};

/* Derived, never stored: the thread-run domain owns whether a thread is running. A row with no
   thread is an ask that has not come back yet. */
export const workflowStepTaskStatus = (state, { workflowId, stepId } = {}) => {
  const task = findWorkflowStepTask(workflowStepTasks(state), { workflowId, stepId });

  if (!task) {
    return 'idle';
  }

  if (!task.threadId) {
    return 'pending';
  }

  if (threadRunState(state, task.threadId) === 'awaiting_approval') {
    return 'needs_input';
  }

  return isThreadRunning(state, task.threadId) ? 'working' : 'idle';
};

export const workflowStepTaskStartedAt = (state, { workflowId, stepId } = {}) => {
  const threadId = workflowStepTaskThreadId(state, { workflowId, stepId });

  return threadId ? threadRunStartedAt(state, threadId) : null;
};

/* The step threads of ONE workflow that the thread-run domain does not hold yet. The server's
   `running` flag exists only to seed this hydration — liveness for display is `isThreadRunning`,
   kept current by the run stream. Excluding already-hydrated threads is what makes this settle.
   Scoped by workflow because the slice keeps every workflow visited this session; memoized because
   it feeds an effect. */
export const workflowStepTaskThreadIdsNeedingHydration = createSelector([workflowStepTasks, threadContentMap, (_state, workflowId) => String(workflowId || '').trim()], (tasks, threadContentMapValue, workflowId) => {
  if (!workflowId) {
    return EMPTY_ARRAY;
  }

  return tasks
    .filter((task) => {
      if (task?.running !== true || !task?.threadId || !String(task?.resourcePrimaryKey || '').startsWith(`${workflowId}:`)) {
        return false;
      }

      return getThreadContentValue({ threadContentMapValue, threadId: task.threadId })?.eventsHydrated !== true;
    })
    .map((task) => task.threadId);
});
